/**
 * ProfileCard - a short summary of a person
 * -----------------------------------------
 * Used in the student directory, and on the applications list where a poster
 * is deciding who to accept.
 */

import { Link } from 'react-router-dom';
import { MapPin, CheckCircle2 } from 'lucide-react';

import Avatar from './Avatar';
import Badge from './Badge';
import StarRating from './StarRating';
import { distance } from '../utils/format';

export default function ProfileCard({ person, distanceKm = null, children, linkTo = null }) {
  if (!person) return null;

  const isStudent = person.role === 'student';
  const href = linkTo || `/${isStudent ? 'students' : 'users'}/${person._id}`;
  const name = `${person.firstName} ${person.lastName}`;

  return (
    <article className="card card-hover">
      <div className="profile-card">
        <Avatar person={person} />

        <div className="profile-card-body">
          <div className="row" style={{ gap: 'var(--space-xs)' }}>
            <Link to={href} className="profile-name">
              {name}
            </Link>
            {isStudent && person.schoolYear && <Badge tone="soft-blue">{person.schoolYear}</Badge>}
          </div>

          <div className="row" style={{ gap: 'var(--space-sm)', marginTop: 2 }}>
            <StarRating value={person.rating?.average || 0} count={person.rating?.count || 0} size={14} />
          </div>

          <div className="card-meta" style={{ marginTop: 6 }}>
            {person.location?.city && (
              <span>
                <MapPin size={14} aria-hidden="true" />
                {person.location.city}
              </span>
            )}
            {distanceKm !== null && distanceKm !== undefined && <span>{distance(distanceKm)}</span>}
            {isStudent && (
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                {person.stats?.choresCompleted || 0} completed
              </span>
            )}
          </div>

          {person.bio && (
            <p className="card-body-text" style={{ marginTop: 'var(--space-xs)' }}>
              {person.bio}
            </p>
          )}

          {Array.isArray(person.skills) && person.skills.length > 0 && (
            <div className="row" style={{ gap: 6, marginTop: 'var(--space-xs)' }}>
              {person.skills.map((skill) => (
                <Badge key={skill} tone="soft-green">
                  {skill}
                </Badge>
              ))}
            </div>
          )}

          {children}
        </div>
      </div>
    </article>
  );
}
